import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import SubscriptionSection from './SubscriptionSection';
import LangFlag from './LangFlag';
import './Settings.css';
import { useLanguage } from '../i18n/LanguageContext';

interface UserSettings {
  email: string;
  name: string;
  theme: string;
  language: string;
}

interface SettingsProps {
  onClose?: () => void;
  initialTab?: 'account' | 'appearance' | 'language' | 'help';
}

const Settings: React.FC<SettingsProps> = ({ onClose, initialTab = 'account' }) => {
  const { t, setLanguage: setContextLanguage } = useLanguage();
  const [activeTab, setActiveTab] = useState<'account' | 'appearance' | 'language' | 'help'>(initialTab);
  const [settings, setSettings] = useState<UserSettings>({
    email: '',
    name: '',
    theme: 'dark',
    language: 'en'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Email change form
  const [newEmail, setNewEmail] = useState('');

  // Feedback form
  const [feedbackKind, setFeedbackKind] = useState<'bug' | 'idea' | 'other'>('bug');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    setMessage(null);
    try {
      await api.post('/feedback/', {
        kind: feedbackKind,
        message: feedbackMessage.trim(),
        platform: 'web',
      });
      setFeedbackSent(true);
      setFeedbackMessage('');
      setMessage({ type: 'success', text: t.settings.feedbackThanks });
    } catch {
      setMessage({ type: 'error', text: t.settings.feedbackError });
    } finally {
      setFeedbackSending(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/user/settings/');
      setSettings(response.data);
      setNewEmail(response.data.email);
    } catch (error) {
      console.error('Failed to load settings:', error);
      showMessage('error', t.settings.failedToLoad);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply theme when settings load
  useEffect(() => {
    if (settings.theme) {
      document.documentElement.setAttribute('data-theme', settings.theme);
    }
  }, [settings.theme]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    // Changing the account email requires the current password
    const currentPassword = window.prompt(t.settings.currentPasswordPrompt);
    if (!currentPassword) return;
    setIsSaving(true);
    try {
      await api.patch('/user/settings/', {
        email: newEmail,
        current_password: currentPassword,
      });
      setSettings({ ...settings, email: newEmail });
      showMessage('success', t.settings.emailUpdated);
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.failedToUpdate);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage('error', t.settings.passwordMismatch);
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      showMessage('error', t.settings.passwordTooShort);
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.post('/user/change-password/', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      // The backend rotates the auth token on password change
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
      }
      showMessage('success', t.settings.passwordChanged);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.failedToChange);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await api.patch('/user/settings/', 
        { name: settings.name }
      );
      showMessage('success', t.settings.nameUpdated);
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.failedToUpdate);
    } finally {
      setIsSaving(false);
    }
  };

  const handleThemeChange = async (theme: string) => {
    setIsSaving(true);
    try {
      await api.patch('/user/settings/', 
        { theme }
      );
      setSettings({ ...settings, theme });
      showMessage('success', t.settings.themeUpdated);
      // Apply theme to document
      document.documentElement.setAttribute('data-theme', theme);
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.failedToUpdate);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLanguageChange = async (language: string) => {
    setIsSaving(true);
    try {
      await api.patch('/user/settings/', 
        { language }
      );
      setSettings({ ...settings, language });
      setContextLanguage(language); // Update context language
      showMessage('success', t.settings.languageUpdated);
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.updateFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const handleDeactivateAccount = async () => {
    if (!window.confirm(t.settings.confirmDeactivate)) {
      return;
    }

    try {
      await api.post('/user/deactivate/', {});
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.failedToDeactivate);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmation = window.prompt(t.settings.deletePrompt);
    if (confirmation !== 'DELETE') {
      return;
    }

    try {
      await api.delete('/user/delete/');
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || t.settings.failedToDeleteAccount);
    }
  };

  if (isLoading) {
    return (
      <div className="settings-container">
        <div className="loading-spinner">{t.settings.loading}</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>{t.settings.title}</h1>
      </div>

      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-tabs">
        <button 
          className={`tab ${activeTab === 'account' ? 'active' : ''}`}
          onClick={() => setActiveTab('account')}
        >
          {t.settings.account}
        </button>
        <button 
          className={`tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          {t.settings.appearance}
        </button>
        <button 
          className={`tab ${activeTab === 'language' ? 'active' : ''}`}
          onClick={() => setActiveTab('language')}
        >
          {t.settings.language}
        </button>
        <button 
          className={`tab ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => setActiveTab('help')}
        >
          {t.settings.help}
        </button>
      </div>

      <div className="settings-content">
        {/* ACCOUNT TAB */}
        {activeTab === 'account' && (
          <div className="settings-section">
            {/* Email Section */}
            <div className="settings-group">
              <h2>{t.settings.emailLabel}</h2>
              <form onSubmit={handleEmailChange}>
                <div className="form-field">
                  <label>{t.settings.currentEmail}</label>
                  <input type="text" value={settings.email} disabled />
                </div>
                <div className="form-field">
                  <label>{t.settings.newEmail}</label>
                  <input 
                    type="email" 
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={t.settings.enterNewEmail}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving ? t.settings.updating : t.settings.updateEmail}
                </button>
              </form>
            </div>

            {/* Password Section */}
            <div className="settings-group">
              <h2>{t.settings.password}</h2>
              <form onSubmit={handlePasswordChange}>
                <div className="form-field">
                  <label>{t.settings.currentPassword}</label>
                  <input 
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    placeholder={t.settings.enterCurrentPassword}
                  />
                </div>
                <div className="form-field">
                  <label>{t.settings.newPassword}</label>
                  <input 
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    placeholder={t.settings.enterNewPassword}
                  />
                </div>
                <div className="form-field">
                  <label>{t.settings.confirmPassword}</label>
                  <input 
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    placeholder={t.settings.confirmNewPassword}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving ? t.settings.updating : t.settings.updatePassword}
                </button>
              </form>
            </div>

            {/* Name Section */}
            <div className="settings-group">
              <h2>{t.settings.name}</h2>
              <form onSubmit={handleNameChange}>
                <div className="form-field">
                  <label>{t.settings.displayName}</label>
                  <input 
                    type="text"
                    value={settings.name}
                    onChange={(e) => setSettings({...settings, name: e.target.value})}
                    placeholder={t.settings.enterName}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={isSaving}>
                  {isSaving ? t.settings.updating : t.settings.updateName}
                </button>
              </form>
            </div>

            {/* Subscription Section */}
            <div className="settings-group">
              <h2>{t.settings.subscription}</h2>
              <SubscriptionSection />
            </div>

            {/* Logout Section */}
            <div className="settings-group">
              <h2>{t.settings.logout}</h2>
              <button 
                className="btn-primary" 
                onClick={handleLogout}
              >
                {t.settings.logoutButton}
              </button>
            </div>

            {/* Account Actions */}
            <div className="settings-group danger-zone">
              <h2>{t.settings.dangerZone}</h2>
              <div className="danger-actions">
                <button 
                  className="btn-danger" 
                  onClick={handleDeactivateAccount}
                >
                  {t.settings.deactivateAccount}
                </button>
                <button 
                  className="btn-danger-red" 
                  onClick={handleDeleteAccount}
                >
                  {t.settings.deleteAccountButton}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* APPEARANCE TAB */}
        {activeTab === 'appearance' && (
          <div className="settings-section">
            <div className="settings-group">
              <h2>{t.settings.selectTheme}</h2>
              <div className="theme-options">
                <button 
                  className={`theme-btn ${settings.theme === 'dark' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  {t.settings.darkTheme}
                </button>
                <button 
                  className={`theme-btn ${settings.theme === 'light' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('light')}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                  </svg>
                  {t.settings.lightTheme}
                </button>
              </div>
              <p className="placeholder-text">{t.settings.moreThemesSoon}</p>
            </div>
          </div>
        )}

        {/* LANGUAGE TAB */}
        {activeTab === 'language' && (
          <div className="settings-section">
            <div className="settings-group">
              <h2>{t.settings.selectLanguage}</h2>
              <div className="language-options">
                <button
                  className={`language-btn ${settings.language === 'en' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('en')}
                >
                  <span className="flag"><LangFlag lang="en" size={16} /></span>
                  {t.settings.english}
                </button>
                <button
                  className={`language-btn ${settings.language === 'es' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('es')}
                >
                  <span className="flag"><LangFlag lang="es" size={16} /></span>
                  {t.settings.spanish}
                </button>
                <button
                  className={`language-btn ${settings.language === 'pt' ? 'active' : ''}`}
                  onClick={() => handleLanguageChange('pt')}
                >
                  <span className="flag"><LangFlag lang="pt" size={16} /></span>
                  {t.settings.portuguese}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HELP TAB */}
        {activeTab === 'help' && (
          <div className="settings-section">
            <div className="settings-group">
              <h2>{t.settings.feedbackTitle}</h2>
              <p className="placeholder-text">{t.settings.feedbackDesc}</p>
              <form onSubmit={handleFeedbackSubmit}>
                <div className="form-field">
                  <select
                    value={feedbackKind}
                    onChange={(e) => { setFeedbackKind(e.target.value as any); setFeedbackSent(false); }}
                  >
                    <option value="bug">{t.settings.feedbackTypeBug}</option>
                    <option value="idea">{t.settings.feedbackTypeIdea}</option>
                    <option value="other">{t.settings.feedbackTypeOther}</option>
                  </select>
                </div>
                <div className="form-field">
                  <textarea
                    rows={5}
                    value={feedbackMessage}
                    onChange={(e) => { setFeedbackMessage(e.target.value); setFeedbackSent(false); }}
                    placeholder={t.settings.feedbackPlaceholder}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={feedbackSending || !feedbackMessage.trim()}>
                  {feedbackSending ? t.settings.feedbackSending : t.settings.feedbackSend}
                </button>
                {feedbackSent && <span className="feedback-sent-note"> ✓ {t.settings.feedbackThanks}</span>}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
